import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useDropzone } from 'react-dropzone'
import DownloadIcon from '@mui/icons-material/Download'
import * as XLSX from 'xlsx'
import axios from 'axios'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import api from '../../services/authService'

interface UploadModalProps {
  open: boolean
  onClose: () => void
  onUpload: (file: File) => void
  title?: string
  acceptedFileTypes?: Record<string, string[]>
  maxFiles?: number
  dropzoneText?: string
  acceptedFileTypesText?: string
  templatePath?: string
  onUploadResult?: (result: { message: string; severity: 'success' | 'error' }) => void
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onUpload,
  title = 'อัพโหลดไฟล์',
  acceptedFileTypes = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
    'application/vnd.ms-excel': ['.xls'],
  },
  maxFiles = 1,
  dropzoneText = 'ลากไฟล์มาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์',
  acceptedFileTypesText = 'รองรับเฉพาะไฟล์ .xlsx และ .xls',
  templatePath = '/templates/site_user_template.xlsx',
  onUploadResult,
}) => {
  const { getRootProps, getInputProps, isDragActive, acceptedFiles, fileRejections, open: openDropzone } =
    useDropzone({
      accept: acceptedFileTypes,
      maxFiles,
    })

  const [localFiles, setLocalFiles] = useState<File[]>([]);

  const requiredFields = [
    'username(required)',
    'password(required)',
    'name(required)',
    'employeeCode(required)',
    // 'projectCode(required)' // ไม่ต้องเช็ค
  ];

  const [excelError, setExcelError] = useState<string | null>(null);

  const expectedHeader = [
    'username(required)',
    'password(required)',
    'projectCode(required)',
    'Image',
    'name(required)',
    'employeeCode(required)',
    'position',
    'email',
    'phoneNumber',
  ];

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      setLocalFiles(Array.from(acceptedFiles));
    }
  }, [acceptedFiles]);

  useEffect(() => {
    if (!open) {
      setLocalFiles([]);
    }
  }, [open]);

  useEffect(() => {
    const checkExcel = async () => {
      setExcelError(null);
      if (localFiles.length > 0) {
        try {
          const file = localFiles[0];
          const data = await file.arrayBuffer();
          const workbook = XLSX.read(data);
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          // ตรวจสอบ header ว่ามีครบทุกช่อง (ไม่สนลำดับ)
          const firstRow = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 0, blankrows: false })[0] as string[] || [];
          const isHeaderCorrect = expectedHeader.every((h) => firstRow.includes(h));
          if (!isHeaderCorrect) {
            setExcelError('Header ไม่ถูกต้อง กรุณาตรวจสอบไฟล์ตัวอย่าง');
            return;
          }

          for (let i = 0; i < json.length; i++) {
            for (const field of requiredFields) {
              if (!json[i][field] || String(json[i][field]).trim() === '') {
                setExcelError(`แถวที่ ${i + 2} ข้อมูล '${field}' ไม่ครบถ้วน`);
                return;
              }
            }
          }
        } catch (e) {
          setExcelError('ไม่สามารถอ่านไฟล์ Excel ได้');
        }
      }
    };
    checkExcel();
  }, [localFiles]);

  const handleUpload = async () => {
    if (localFiles.length > 0) {
      try {
        const file = localFiles[0]
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data)
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const json: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' })

        // Map Excel columns to API fields
        const items = json.map((row) => ({
          username: row['username(required)'] || '',
          password: row['password(required)'] || '',
          image: row['Image'] || '',
          phoneNumber: row['phoneNumber'] || '',
          name: row['name(required)'] || '',
          employeeCode: row['employeeCode(required)'] || '',
          position: row['position'] || '',
          email: row['email'] || '',
          projectCode: row['projectCode(required)'] ? row['projectCode(required)'] : 'HQ01',
        }))

        // ส่งข้อมูลไปยัง API
        const response = await api.post('/projects-users/import', { items })
        console.log(response)
        if (onUploadResult) {
          onUploadResult({ message: 'อัปโหลดสมบูรณ์', severity: 'success' });
        }
        onClose()
      } catch (error: any) {
        if (onUploadResult) {
          onUploadResult({
            message: `เกิดข้อผิดพลาดในการอัปโหลด ${error?.response?.data?.message}`,
            severity: 'error',
          });
        }
      }
    }
  }

  const handleDownloadTemplate = () => {
    const link = document.createElement('a')
    link.href = templatePath
    link.download = 'site_user_template.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleRemoveFile = () => {
    setLocalFiles([]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? '#f0f0f0' : 'transparent',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body1">
            {isDragActive ? 'วางไฟล์ที่นี่...' : dropzoneText}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {acceptedFileTypesText}
          </Typography>
        </Box>
        {excelError && (
          <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
            {excelError}
          </Typography>
        )}
        {localFiles.length > 0 && !excelError && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Typography variant="body2">
              ไฟล์ที่เลือก: {localFiles[0].name}
            </Typography>
            <Button color="error" size="small" onClick={handleRemoveFile}>
              ลบไฟล์
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', mx: 2, mb: 1 }}>
        <Button
          onClick={handleDownloadTemplate}
          startIcon={<DownloadIcon />}
        >
          ดาวน์โหลด Template
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={localFiles.length === 0 || !!excelError}
          >
            อัพโหลด
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default UploadModal
